import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';

// Acknowledgement is versioned so we can re-prompt users if the cookie policy
// changes materially. Bump the suffix when the cookie list in /legal/cookies
// changes (new categories, new third parties, etc).
const STORAGE_KEY = 'cookie_notice_ack_v1';

// Don't show the banner on these paths. The client menu (/m/:slug/:token) is
// a public consumer surface — a cookie bar there would feel intrusive and out
// of context. /legal/cookies is the policy page itself — banner would be
// redundant.
const HIDDEN_PATH_PREFIXES = ['/m/', '/legal/cookies'];

function pathShouldHideBanner(pathname) {
    return HIDDEN_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(p));
}

export function CookieNoticeBanner() {
    const { pathname } = useLocation();
    const [visible, setVisible] = useState(false);

    // Hydrate visibility from localStorage on mount + on path changes. We delay
    // the initial render slightly so the page transition doesn't fight with the
    // slide-in animation.
    useEffect(() => {
        if (pathShouldHideBanner(pathname)) {
            setVisible(false);
            return;
        }
        let acked = null;
        try { acked = localStorage.getItem(STORAGE_KEY); } catch {}
        if (acked) {
            setVisible(false);
            return;
        }
        const t = setTimeout(() => setVisible(true), 350);
        return () => clearTimeout(t);
    }, [pathname]);

    const acknowledge = () => {
        try {
            localStorage.setItem(STORAGE_KEY, new Date().toISOString());
        } catch {}
        setVisible(false);
    };

    if (!visible) return null;

    return (
        <div
            role="dialog"
            aria-live="polite"
            aria-label="Notificare cookies"
            data-testid="cookie-notice-banner"
            style={{
                position: 'fixed',
                left: 12, right: 12,
                bottom: 'max(12px, env(safe-area-inset-bottom, 0px))',
                zIndex: 1000,
                maxWidth: 760,
                margin: '0 auto',
                padding: '0.9rem 1rem',
                background: 'rgba(15, 20, 24, 0.97)',
                backdropFilter: 'blur(14px)',
                border: '1px solid var(--border-strong)',
                borderRadius: 14,
                boxShadow: '0 12px 36px rgba(0, 0, 0, 0.55)',
                color: 'var(--text)',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                fontSize: '0.88rem',
                lineHeight: 1.5,
                animation: 'cookie-notice-rise 0.28s ease-out',
            }}
        >
            <div style={{ flexShrink: 0, color: 'var(--amber)' }}>
                <Cookie size={22} />
            </div>
            <div style={{ flex: 1, color: 'var(--text-muted)' }}>
                Folosim cookies <strong style={{ color: 'var(--text)' }}>strict necesare</strong> pentru
                autentificare și cookies de <strong style={{ color: 'var(--text)' }}>analytics anonimizate</strong>{' '}
                (Google Analytics 4, IP anonimizat) pentru a măsura traficul. Nu folosim cookies de marketing sau publicitate.{' '}
                <Link to="/legal/cookies" style={{ color: 'var(--amber)', textDecoration: 'underline' }}>
                    Detalii
                </Link>.
            </div>
            <button
                type="button"
                onClick={acknowledge}
                data-testid="cookie-notice-ack"
                className="btn btn-primary btn-sm"
                style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
            >
                Am înțeles
            </button>
            <button
                type="button"
                onClick={acknowledge}
                aria-label="Închide"
                data-testid="cookie-notice-close"
                style={{
                    flexShrink: 0,
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--text-faint)',
                    cursor: 'pointer',
                    padding: 4,
                    display: 'inline-flex',
                }}
            >
                <X size={16} />
            </button>
            <style>{`
                @keyframes cookie-notice-rise {
                    from { transform: translateY(20px); opacity: 0; }
                    to   { transform: translateY(0);    opacity: 1; }
                }
                @media (max-width: 540px) {
                    [data-testid="cookie-notice-banner"] {
                        flex-wrap: wrap;
                    }
                    [data-testid="cookie-notice-banner"] > :nth-child(2) {
                        flex-basis: 100%;
                        order: 1;
                    }
                }
            `}</style>
        </div>
    );
}
